'use client'

import { CTACard } from '@/components/cta/cta-card'
import { BackgroundGradient } from '@/components/ui/background-gradient'
import { BentoCard, BentoGrid } from '@/components/ui/bento-grid'
import { Button } from '@/components/ui/button'
import { DotBackground } from '@/components/ui/dot-background'
import { GridBackground } from '@/components/ui/grid-background'
import { Input } from '@/components/ui/input'
import { Reveal } from '@/components/ui/reveal'
import { TypingAnimation } from '@/components/ui/typing-animation'
import {
  BodyText,
  LabelText,
  PageHeading,
  SectionHeading
} from '@/components/ui/typography'
import {
  IconArrowRight,
  IconBolt,
  IconBook2,
  IconCards,
  IconFileText,
  IconSearch
} from '@tabler/icons-react'
import { motion } from 'motion/react'
import { Link } from '@/i18n/routing'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'
import { StoryCard } from '@/components/story-card'
import type { StoryMetadata } from '@/content/stories'

interface LandingClientProps {
  latestStories: StoryMetadata[]
}

export function LandingClient({ latestStories }: LandingClientProps) {
  const t = useTranslations('landing')
  const tCta = useTranslations('cta')

  return (
    <main className="flex flex-1 flex-col">
      <GridBackground>
        <section className="px-4 pt-20 pb-16 sm:pt-32 sm:pb-28">
          <div className="mx-auto w-full max-w-6xl">
            <div className="flex flex-col gap-12 lg:flex-row lg:items-center">
              <div className="flex flex-1 flex-col gap-6">
                <LabelText className="text-primary/80 text-xs tracking-widest uppercase">
                  {t('tagline')}
                </LabelText>
                <PageHeading className="text-foreground text-5xl leading-[1.03] font-semibold text-balance sm:text-6xl lg:text-7xl">
                  {t('heading')}
                </PageHeading>
                <BodyText className="text-muted-foreground text-xl sm:text-2xl">
                  {t('description')}
                </BodyText>
                <div className="flex flex-wrap gap-4">
                  <BackgroundGradient containerClassName="rounded-xl">
                    <Button size="lg" asChild>
                      <Link href="/dict">{tCta('primary.button')}</Link>
                    </Button>
                  </BackgroundGradient>
                  <Button size="lg" variant="outline" asChild>
                    <Link href="/read">{tCta('stories.button')}</Link>
                  </Button>
                </div>
              </div>
              <FeatureStackWithTranslations />
            </div>
          </div>
        </section>
      </GridBackground>

      <section className="bg-background border-t px-4 py-16">
        <Reveal
          className="mx-auto flex w-full max-w-6xl flex-col gap-8"
          viewportAmount={0.1}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <div className="mb-6 space-y-2">
            <p className="text-primary/80 text-sm font-semibold tracking-widest uppercase">
              {t('whyChoose')}
            </p>
            <h2 className="text-foreground text-2xl leading-tight font-semibold">
              {t('subheading')}
            </h2>
          </div>
          <Reveal
            variant="fadeIn"
            delay={0.1}
            viewportAmount={0.4}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <BentoGrid className="auto-rows-[12rem] grid-cols-2 gap-4 lg:grid-cols-3">
              <LandingFeatureCards />
            </BentoGrid>
          </Reveal>
        </Reveal>
      </section>

      <DotBackground>
        <section className="px-4 py-16">
          <Reveal
            className="mx-auto flex w-full max-w-6xl flex-col gap-6"
            viewportAmount={0.3}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <SectionHeading className="border-0 pb-0 text-3xl">
                  {t('latestStories')}
                </SectionHeading>
                <BodyText className="text-muted-foreground">
                  {t('storiesDescription')}
                </BodyText>
              </div>
              <Button variant="outline" asChild>
                <Link href="/read">
                  {t('viewLibrary')}
                  <IconArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {latestStories.map((story) => (
                <StoryCard
                  key={story.slug}
                  story={story}
                  readStoryText={t('readStory')}
                />
              ))}
            </div>
          </Reveal>
        </section>
      </DotBackground>

      {/* CTA Section */}
      <section className="px-4 py-16">
        <Reveal
          className="mx-auto flex w-full max-w-6xl"
          viewportAmount={0.3}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <CTACard variant="primary" />
        </Reveal>
      </section>
    </main>
  )
}

function LandingFeatureCards() {
  const t = useTranslations('landing')
  const loginFeatureCards = [
    {
      name: t('smartSearch.name'),
      className: 'col-span-2 sm:col-span-1 lg:col-span-2',
      background: (
        <div className="from-primary/20 via-primary/5 to-background/80 pointer-events-none absolute inset-0 rounded-[inherit] bg-linear-to-br p-4">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Input
                readOnly
                value=""
                className="border-border/60 bg-background/70 text-foreground h-12 w-full rounded-xl border pr-16 pl-12 text-base font-medium shadow-none placeholder:text-transparent"
              />
              <IconSearch className="text-muted-foreground absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2" />
              <TypingAnimation
                className="text-muted-foreground absolute top-1/2 left-12 -translate-y-1/2 text-base font-medium"
                words={['你好', 'ni3 hao3', 'ㄋㄧˇ ㄏㄠˇ', 'hello']}
                typeSpeed={80}
                deleteSpeed={40}
                pauseDelay={900}
                loop
                startOnView
              />
              <span className="text-muted-foreground/70 absolute top-1/2 right-4 flex -translate-y-1/2 items-center gap-1 text-xs font-semibold tracking-widest uppercase">
                全
                <IconArrowRight className="h-3 w-3" />
              </span>
            </div>
            <div className="text-muted-foreground hidden text-xs font-medium lg:block">
              {t('smartSearch.worksOnlineOffline')}
            </div>
          </div>
        </div>
      ),
      Icon: () => '',
      description: t('smartSearch.description'),
      href: '/dict',
      cta: t('smartSearch.cta')
    },
    {
      name: t('curatedStories.name'),
      className: 'col-span-2 sm:col-span-1 lg:col-span-1',
      background: <div className="bg-muted/30 absolute inset-0" />,
      Icon: IconBook2,
      description: t('curatedStories.description'),
      href: '/read',
      cta: t('curatedStories.cta')
    },
    {
      name: t('documentReader.name'),
      className: 'col-span-2 sm:col-span-1 lg:col-span-1',
      background: <div className="bg-muted/30 absolute inset-0" />,
      Icon: IconFileText,
      description: t('documentReader.description'),
      href: '/read/import',
      cta: t('documentReader.cta')
    },
    {
      name: t('flashcards.name'),
      className: 'col-span-2 sm:col-span-1 lg:col-span-2',
      background: <div className="bg-muted/30 absolute inset-0" />,
      Icon: IconCards,
      description: t('flashcards.description'),
      href: '/collections',
      cta: t('flashcards.cta')
    }
  ]

  return (
    <>
      {loginFeatureCards.map((card) => (
        <BentoCard key={card.name} {...card} />
      ))}
    </>
  )
}

function FeatureStackWithTranslations() {
  const t = useTranslations('landing')

  const showcaseCards = [
    {
      name: t('dictionaryFeature.name'),
      description: t('dictionaryFeature.description'),
      detail: t('dictionaryFeature.detail'),
      accent: 'from-rose-200/40 via-primary/10 to-background',
      metrics: { label: t('dictionaryFeature.entriesCached'), value: '120k+' },
      preview: <DictionaryPreview />,
      badges: ['Hanzi', 'Pinyin', 'Zhuyin'],
      Icon: IconSearch
    },
    {
      name: t('storiesFeature.name'),
      description: t('storiesFeature.description'),
      detail: t('storiesFeature.detail'),
      accent: 'from-orange-200/40 via-orange-100/20 to-background',
      metrics: { label: t('storiesFeature.curatedStories'), value: '20+' },
      preview: <StoryPreview />,
      badges: [t('badges.audioIncluded')],
      Icon: IconBook2
    },
    {
      name: t('flashcardsFeature.name'),
      description: t('flashcardsFeature.description'),
      detail: t('flashcardsFeature.detail'),
      accent: 'from-sky-200/30 via-sky-100/15 to-background',
      metrics: { label: t('flashcardsFeature.cardsDueToday'), value: '18' },
      preview: <FlashcardPreview />,
      badges: [t('badges.spacedRepetition')],
      Icon: IconBolt
    }
  ]

  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)
  const cardCount = showcaseCards.length
  const nextCard = useCallback(
    () => setActive((prev) => (prev + 1) % cardCount),
    [cardCount]
  )

  useEffect(() => {
    if (paused) return
    const id = setInterval(nextCard, 3000)
    return () => clearInterval(id)
  }, [nextCard, paused])

  return (
    <div
      className="relative flex flex-1 items-center justify-center lg:justify-end"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="button"
      tabIndex={0}
      onClick={nextCard}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          nextCard()
        }
      }}
    >
      <div className="relative h-[460px] w-full max-w-[520px]">
        {showcaseCards.map((card, index) => {
          const position =
            (index - active + showcaseCards.length) % showcaseCards.length
          const isActive = position === 0

          return (
            <motion.div
              key={card.name}
              className="bg-card/95 border-border/40 absolute inset-x-0 mx-auto w-full rounded-3xl border p-5 shadow-xl backdrop-blur"
              style={{ zIndex: showcaseCards.length - position }}
              animate={{
                y: position * 24,
                scale: isActive ? 1 : 0.96,
                opacity: Math.max(0.35, 1 - position * 0.3)
              }}
              transition={{ type: 'spring', stiffness: 220, damping: 24 }}
            >
              <div
                className={`absolute inset-0 rounded-[inherit] bg-linear-to-br ${card.accent} opacity-70`}
              />
              <div className="relative flex flex-col gap-4">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <card.Icon className="text-primary h-5 w-5" />
                  {card.name}
                </div>
                <h3 className="text-lg font-semibold">{card.detail}</h3>
                <p className="text-muted-foreground text-sm">
                  {card.description}
                </p>
                {card.preview}
                {card.metrics && (
                  <div className="text-primary/80 flex items-center justify-between text-[0.7rem] font-semibold tracking-[0.2em] uppercase">
                    <span>{card.metrics.label}</span>
                    <span>{card.metrics.value}</span>
                  </div>
                )}
                {card.badges && (
                  <div className="flex flex-wrap gap-2">
                    {card.badges.map((badge) => (
                      <span
                        key={badge}
                        className="bg-background/70 text-foreground/80 rounded-full px-3 py-1 text-xs font-medium"
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

function DictionaryPreview() {
  return (
    <div className="bg-background/80 border-border/50 rounded-2xl border p-4 opacity-60 shadow-inner">
      <div className="flex items-baseline gap-3">
        <span className="font-chinese text-2xl font-semibold text-rose-600 dark:text-rose-400">
          离不开
        </span>
        <span className="text-muted-foreground text-sm">lí bù kāi</span>
      </div>
      <div className="text-muted-foreground mt-1 text-xs">ㄌㄧˊ ㄅㄨˋ ㄎㄞ</div>
      <div className="mt-3 space-y-1.5">
        <div className="text-foreground text-sm">
          <span className="text-muted-foreground mr-1.5 text-xs">[v.]</span>
          inseparable; can&apos;t do without
        </div>
        <div className="text-muted-foreground text-xs italic">
          我离不开咖啡。 - I can&apos;t live without coffee.
        </div>
      </div>
    </div>
  )
}

function StoryPreview() {
  return (
    <div className="bg-background/80 border-border/50 rounded-2xl border p-4 opacity-60 shadow-inner">
      <div className="font-chinese flex flex-wrap gap-1 text-lg leading-relaxed">
        <span className="rounded bg-amber-100/80 px-1 dark:bg-amber-900/40">
          今天
        </span>
        <span>是</span>
        <span className="rounded bg-rose-100/80 px-1 dark:bg-rose-900/40">
          星期一
        </span>
        <span>，</span>
        <span>小明</span>
        <span className="rounded bg-sky-100/80 px-1 dark:bg-sky-900/40">
          很早
        </span>
        <span>就</span>
        <span className="rounded bg-amber-100/80 px-1 dark:bg-amber-900/40">
          起床
        </span>
        <span>了</span>
        <span>。</span>
      </div>
      <p className="text-muted-foreground mt-2 text-sm italic">
        Today is Monday. Xiao Ming woke up very early.
      </p>
    </div>
  )
}

function FlashcardPreview() {
  return (
    <div className="bg-background/80 border-border/50 rounded-2xl border p-4 opacity-60 shadow-inner">
      <div className="bg-muted/50 flex min-h-[70px] items-center justify-center rounded-xl p-3 shadow-inner">
        <div className="flex flex-col items-center gap-1">
          <span className="font-chinese text-3xl font-semibold text-rose-600 dark:text-rose-400">
            饺子
          </span>
          <span className="text-muted-foreground text-sm">jiǎo zi</span>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2">
        <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-center text-xs font-medium dark:border-red-900/50 dark:bg-red-950/30">
          <div>Again</div>
          <div className="text-muted-foreground text-[0.65rem]">1m</div>
        </div>
        <div className="rounded-md border border-orange-200 bg-orange-50 px-2 py-1.5 text-center text-xs font-medium dark:border-orange-900/50 dark:bg-orange-950/30">
          <div>Hard</div>
          <div className="text-muted-foreground text-[0.65rem]">6m</div>
        </div>
        <div className="rounded-md border border-green-200 bg-green-50 px-2 py-1.5 text-center text-xs font-medium dark:border-green-900/50 dark:bg-green-950/30">
          <div>Good</div>
          <div className="text-muted-foreground text-[0.65rem]">10m</div>
        </div>
        <div className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-center text-xs font-medium dark:border-blue-900/50 dark:bg-blue-950/30">
          <div>Easy</div>
          <div className="text-muted-foreground text-[0.65rem]">4d</div>
        </div>
      </div>
    </div>
  )
}
